// frontend/src/pages/Payments.jsx
import React, {useEffect, useState} from 'react';
import {
    Box,
    Button,
    CircularProgress,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Menu,
    MenuItem,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography
} from '@mui/material';
import {DataGrid} from '@mui/x-data-grid';
import {formatCurrency, formatDateOnlyISO, useIsMobile} from "../../utils/helpers";
import {api} from "../../services/apiService";
import {useNavigate} from "react-router-dom";
import {API_BASE_URL} from "../../config";

export default function Payments() {
    const isSmall = useIsMobile();
    const navigate = useNavigate();

    const now = new Date();
    const [period, setPeriod] = useState({
        start: `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`,
        end: `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate() + 1).padStart(2, '0')}`,
    });
    const [payments, setPayments] = useState([]);
    const [loading, setLoading] = useState(false);

    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(25);
    const [rowCount, setRowCount] = useState(0);

    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedRow, setSelectedRow] = useState(null);
    const [tvaDialogOpen, setTvaDialogOpen] = useState(false);

    useEffect(() => {
        if (!period.start || !period.end) return;
        setLoading(true);

        api.get(`invoices/get_payments?start=${period.start}&end=${period.end}&per_page=${pageSize}&page=${page + 1}`)
            .then((response) => {
                setPayments(response.data.payments);
                setRowCount(response.data.total);
            })
            .finally(() => setLoading(false));
    }, [period.start, period.end, page, pageSize]);

    const total = payments.reduce((s, p) => s + (Number(p.amount) || 0), 0);
    const totalTva = payments.reduce((s, p) => s + (Number(p.tva?.total) || 0), 0);

    const tvaSummary = Object.values(payments.reduce((acc, payment) => {
        const details = payment.tva?.details ?? [];

        details.forEach((detail) => {
            const rate = detail.rate ?? 'Non défini';
            const amount = Number(detail.amount) || 0;

            if (!acc[rate]) {
                acc[rate] = {rate, base: 0, total: 0};
            }

            acc[rate].base += amount / (rate / 100 || 1);
            acc[rate].total += amount;
        });

        return acc;
    }, {}));

    const columns = [
        {
            field: 'invoice',
            headerName: 'Facture',
            flex: 0.8,
            minWidth: 130,
            valueGetter: (params) => params.ref
        },
        {
            field: 'booking',
            headerName: 'Réservation',
            flex: 0.8,
            minWidth: 130,
            valueGetter: (params) => params.ref
        },
        {field: 'method', headerName: 'Moyen de paiement', flex: 0.7, minWidth: 150},
        {
            field: 'amount',
            headerName: 'Montant',
            flex: 0.6,
            minWidth: 110,
            valueFormatter: (params) => formatCurrency(params),
        },
        {
            field: 'tva',
            headerName: 'Montant TVA',
            flex: 0.6,
            minWidth: 110,
            valueFormatter: (params) => formatCurrency(params.total),
        },
        {field: 'client', headerName: 'Client', flex: 1, minWidth: 150},
        {
            field: 'date',
            headerName: 'Date',
            flex: 0.7,
            minWidth: 110,
            valueFormatter: (params) => formatDateOnlyISO(params)
        },
    ];

    const handleRowClick = (event, row) => {
        setSelectedRow(row);
        setAnchorEl(event.currentTarget || event.target);
    };

    const handleClose = () => {
        setAnchorEl(null);
        setSelectedRow(null);
    };

    const openInvoice = () => {
        if (!selectedRow) return handleClose();

        let kind;
        if (selectedRow.invoice.type === 'final') {
            kind = 'get_final_invoice';
        } else {
            kind = 'get_deposit_invoice';
        }

        window.open(`${API_BASE_URL}/invoices/${kind}/${selectedRow.booking.id}`, '_blank');
        handleClose();
    };

    const openBooking = () => {
        if (!selectedRow) return handleClose();

        const bookingId = selectedRow.booking.id;
        navigate(`/bookings/details/${bookingId}`);
        handleClose();
    };

    return (
        <Container maxWidth="lg">
            <Typography variant="h1">
                Paiements
            </Typography>

            <Box
                component={Paper}
                elevation={2}
                sx={{
                    display: 'flex',
                    flexDirection: isSmall ? 'column' : 'row',
                    alignItems: isSmall ? 'stretch' : 'center',
                    justifyContent: 'space-between',
                    gap: 2,
                    p: 2,
                    mb: 2,
                }}
            >
                <Box>
                    <Typography variant="subtitle2">Résumé de la période</Typography>
                    <Stack sx={{
                        flexDirection: isSmall ? 'column' : 'row',
                        alignItems: isSmall ? 'flex-start' : 'baseline',
                        gap: 1
                    }}>
                        <Typography variant="h6">{formatCurrency(total)}</Typography>
                        <Typography variant="body2" color="text.secondary">
                            dont {formatCurrency(totalTva)} de TVA
                        </Typography>
                    </Stack>
                    <Typography variant="body2" color="text.secondary">
                        {payments.length} paiement{payments.length > 1 ? 's' : ''}
                    </Typography>
                </Box>

                <Box sx={{
                    display: 'flex',
                    gap: 2,
                    alignItems: isSmall ? 'flex-start' : 'center',
                    mt: isSmall ? 1 : 0,
                    flexDirection: isSmall ? 'column' : 'row'
                }}>
                    <TextField
                        label="Début"
                        type="date"
                        value={period.start}
                        onChange={(e) => setPeriod((prev) => ({...prev, start: e.target.value}))}
                        size="small"
                        InputLabelProps={{shrink: true}}
                    />
                    <TextField
                        label="Fin"
                        type="date"
                        value={period.end}
                        onChange={(e) => setPeriod((prev) => ({...prev, end: e.target.value}))}
                        size="small"
                        InputLabelProps={{shrink: true}}
                    />
                    <Button variant="outlined" sx={{py: "6.5px"}} onClick={() => setTvaDialogOpen(true)}>
                        Détails TVA
                    </Button>
                    {loading && <CircularProgress size={24}/>}
                </Box>
            </Box>

            <Paper sx={{height: '60vh', width: '100%'}}>
                <DataGrid
                    rows={payments}
                    columns={columns}
                    rowsPerPageOptions={[25, 50, 100]}
                    paginationModel={{page: page, pageSize: pageSize}}
                    paginationMode="server"
                    rowCount={rowCount}
                    onPaginationModelChange={(model) => {
                        setPage(model.page);
                        setPageSize(model.pageSize);
                    }}
                    onRowClick={(params, event) => handleRowClick(event, params.row)}
                    sx={{
                        border: 0,
                        cursor: "pointer",
                        "& .MuiDataGrid-row:hover": {
                            backgroundColor: "#f0f0f0",
                        },
                        backgroundColor: "#fff",
                    }}
                />
            </Paper>

            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleClose}
                anchorOrigin={{vertical: 'bottom', horizontal: 'left'}}
                transformOrigin={{vertical: 'top', horizontal: 'left'}}
            >
                <MenuItem onClick={openInvoice}>Ouvrir la facture</MenuItem>
                <MenuItem onClick={openBooking}>Ouvrir la réservation</MenuItem>
            </Menu>

            <Dialog open={tvaDialogOpen} onClose={() => setTvaDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Détails TVA par taux</DialogTitle>
                <DialogContent dividers>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Taux</TableCell>
                                <TableCell align="right">Base imposable</TableCell>
                                <TableCell align="right">Montant TVA</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {tvaSummary.map((item) => (
                                <TableRow key={item.rate}>
                                    <TableCell>{item.rate.toFixed(2)} %</TableCell>
                                    <TableCell align="right">{formatCurrency(item.base)}</TableCell>
                                    <TableCell align="right">{formatCurrency(item.total)}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setTvaDialogOpen(false)}>Fermer</Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
}
